import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import CountryFlag from 'src/components/CountryFlag'
import {
  MAP_EDGE_INSET,
  MAP_LAYER,
  MapCountriesValue,
  OPENING_CITY,
  operatingCities,
} from './consts'

export type MapControlsProps = {
  /** The find-a-vehicle box, stacked under the controls in the same cluster. */
  search: React.ReactNode
  onChangeCountryLocationSelector: (value: MapCountriesValue) => void
  /**
   * Whether the browser has told us where the operator is. Without it, "My
   * location" is offered but cannot be honoured, and the map used to answer by
   * flying to Tallinn and calling that the operator's location.
   */
  hasUserLocation: boolean
}

// ----------------------------------------------------------------------
// Where the map is looking.
//
// The city list and the find-a-vehicle box both answer "show me this", so they
// share one piece of console furniture: a flat surface with a hairline,
// floating over the map. The surface is not decoration. A bare floating input
// puts its own label half over the map tiles, where a road name behind the
// word "Centre" makes it unreadable, and at the top edge of the map it is
// clipped as well.
//
// **The list has no printed label.** It carried a static `InputLabel` reading
// "Center", which is what a form field gets because a form field sits in a
// column of other fields and has to say which one it is. A single control
// floating on a map is not in a column, and the word cost a line of type above
// it and the surface the line stood on. The name is on the control instead,
// where assistive tech reads it and nothing has to be drawn.
//
// **The button that frames the fleet is not here.** It was in this cluster,
// beside the city list, which said the two were settings of one thing. One is
// a place you choose; the other is the one control that answers *where are my
// cars*. It stands alone in the opposite corner — see `MapRecentre`.
// ----------------------------------------------------------------------

const MapControls = ({
  search,
  onChangeCountryLocationSelector,
  hasUserLocation,
}: MapControlsProps) => {
  // Starts on the city the map opens on, so the control reports where the map
  // is rather than where it is not.
  const [country, setCountry] = React.useState<MapCountriesValue>(OPENING_CITY)

  const handleChangeValue = (event: SelectChangeEvent) => {
    const { value } = event.target
    onChangeCountryLocationSelector(value as MapCountriesValue)
    setCountry(value as MapCountriesValue)
  }

  return (
    <Box
      sx={(theme) => ({
        position: 'absolute',
        zIndex: MAP_LAYER.furniture,
        top: theme.spacing(MAP_EDGE_INSET),
        left: theme.spacing(MAP_EDGE_INSET),
        display: 'flex',
        flexDirection: 'column',
        // `flex-start`, so the control surface sizes to its own contents rather
        // than stretching to the width of the search box below it.
        alignItems: 'flex-start',
        gap: 1,
        // The map's width less its two insets on a phone, so the search under
        // the city list has the line. Written as a function inside the
        // breakpoint object it was dropped, the cluster shrank to the city
        // list's 122px and the search read `Find ...` (found on 2026-10-31).
        width: {
          xs: `calc(100% - ${theme.spacing(MAP_EDGE_INSET * 2)})`,
          sm: 'auto',
        },
      })}
    >
      {/*
        One cluster, laid out rather than positioned. The search box used to
        float separately at a top inset chosen to clear this surface, so
        changing the height of either one moved the other by hand.
      */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: (theme) => theme.radii.surface,
          boxShadow: (theme) => theme.customShadows.z8,
        }}
      >
        <Select
          value={country}
          onChange={handleChangeValue}
          // The name the label used to print. A control that is alone on a map
          // still has to say what it is to anything that cannot see where it
          // is sitting. Through `inputProps`, which is where MUI puts it on
          // the element that carries the combobox role — on the root it lands
          // on a wrapper with no role to take a name.
          inputProps={{ 'aria-label': 'Centre the map on' }}
          sx={{
            // `background.paper`, not `common.white`: the map keeps its own
            // light basemap in both schemes, but this control is console
            // furniture and a white box on a dark console is the drift.
            backgroundColor: 'background.paper',
            minWidth: 56,
          }}
        >
          {(Object.keys(operatingCities) as MapCountriesValue[]).map((city) => (
            <MenuItem
              key={city}
              value={city}
              // Offered only while it can be honoured. The alternative is a
              // control that answers a different question than the one it asks.
              disabled={city === 'userLocation' && !hasUserLocation}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75}>
                {/* No hover hint on this one: the city's name is the very next
                    element, so a tooltip naming the country would repeat the
                    row back to the pointer. */}
                <CountryFlag
                  code={operatingCities[city].country}
                  size="chip"
                  describe={false}
                />
                <Typography>{operatingCities[city].label}</Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </Box>

      {search}
    </Box>
  )
}

export { MapControls }
